import i18next, { type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';

export const LANGUAGES = ['en', 'sv'] as const;
export type Language = (typeof LANGUAGES)[number];

/** English ships with the app; Swedish is loaded when needed. */
const loaders: Record<Language, () => Promise<Record<string, unknown>>> = {
  en: async () => en,
  sv: async () => (await import('./sv.json')).default,
};

/**
 * Texts of the pages few people open (Administration, CSV import, the
 * set-password page). They load with those pages, which keeps the first
 * screen small.
 */
const extraLoaders: Record<Language, () => Promise<Record<string, unknown>>> = {
  en: async () => (await import('./en.extra.json')).default,
  sv: async () => (await import('./sv.extra.json')).default,
};
const extraLoaded = new WeakMap<I18n, Set<Language>>();

async function addExtra(i18n: I18n, language: Language): Promise<void> {
  const done = extraLoaded.get(i18n);
  if (!done || done.has(language)) return;
  i18n.addResourceBundle(language, 'translation', await extraLoaders[language](), true, true);
  done.add(language);
}

/** Loads the extra texts for the current language, and for every language switched to later. */
export async function loadExtraTexts(i18n: I18n): Promise<void> {
  if (!extraLoaded.has(i18n)) extraLoaded.set(i18n, new Set());
  await Promise.all([...new Set<Language>(['en', currentLanguage(i18n)])].map((l) => addExtra(i18n, l)));
}

export function extraTextsLoaded(i18n: I18n): boolean {
  return extraLoaded.get(i18n)?.has(currentLanguage(i18n)) ?? false;
}

function currentLanguage(i18n: I18n): Language {
  return i18n.language === 'sv' ? 'sv' : 'en';
}

/** Makes sure a language's texts are loaded, then switches to it. */
export async function loadLanguage(i18n: I18n, language: Language): Promise<void> {
  if (!i18n.hasResourceBundle(language, 'translation')) {
    i18n.addResourceBundle(language, 'translation', await loaders[language](), true, true);
  }
  await addExtra(i18n, language);
  if (i18n.language !== language) await i18n.changeLanguage(language);
}

/** The admin's fallback language, which the server writes on <html> (Site defaults). */
export function siteFallbackLanguage(): Language {
  return typeof document !== 'undefined' && document.documentElement.dataset.fallbackLang === 'sv' ? 'sv' : 'en';
}

/** Picks the first supported browser language, else the site's fallback language. */
export function browserLanguage(languages: readonly string[] = navigator.languages ?? [navigator.language], fallback: Language = siteFallbackLanguage()): Language {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'sv' || base === 'en') return base;
  }
  return fallback;
}

/** Stretches text by about 40% and brackets it, to find layouts that overflow. */
export function pseudoLocalize(text: string): string {
  const padded = text.replace(/[aeiouAEIOU]/g, (c) => c + c);
  const extra = '~'.repeat(Math.max(0, Math.ceil(text.length * 0.4) - (padded.length - text.length)));
  return `[${padded}${extra}]`;
}

export function createI18n(
  language: Language = 'en',
  options: { pseudo?: boolean; extra?: Partial<Record<Language, Record<string, unknown>>> } = {},
): I18n {
  const instance = i18next.createInstance();
  instance.use(initReactI18next);
  if (options.pseudo) {
    instance.use({ type: 'postProcessor', name: 'pseudo', process: (value: string) => pseudoLocalize(value) });
  }
  const resources: Record<string, { translation: Record<string, unknown> }> = { en: { translation: en } };
  for (const [lang, bundle] of Object.entries(options.extra ?? {})) resources[lang] = { translation: bundle };
  void instance.init({
    resources,
    lng: language,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
    ...(options.pseudo ? { postProcess: ['pseudo'] } : {}),
  });
  return instance;
}
